'use client';

import { useEffect, useState, useCallback } from 'react';
import { getToken } from '@/lib/auth';
import {
  fetchDashboard,
  fetchActiveMiners,
  DashboardStats,
  SparklinePoint,
  ActiveMiner,
} from '@/lib/adminApi';
import StatCard from '@/components/StatCard';
import MiningChart from '@/components/MiningChart';
import ActiveMinersTable from '@/components/ActiveMinersTable';

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [sparkline, setSparkline] = useState<SparklinePoint[]>([]);
  const [miners, setMiners] = useState<ActiveMiner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      setError('');
      const token = getToken();
      const [dash, active] = await Promise.all([
        fetchDashboard(token!),
        fetchActiveMiners(token!),
      ]);
      setStats(dash.stats);
      setSparkline(dash.sparkline);
      setMiners(active.miners);
      setLastUpdated(new Date());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      load();
    }, 0);
    const interval = setInterval(load, 30000);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [load]);

  if (loading) {
    return <div style={{ color: '#A0A0A0' }}>Loading dashboard...</div>;
  }

  if (error) {
    return <div style={{ color: '#EF4444' }}>{error}</div>;
  }

  return (
    <div>
      <div style={{
        marginBottom: 32,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}>
        <div>
          <h1 style={{
            margin: 0, color: '#F5F5F5',
            fontSize: 28, fontWeight: 700,
          }}>
            Dashboard
          </h1>
          <p style={{
            margin: '4px 0 0', color: '#6B6B6B', fontSize: 13,
          }}>
            Overview of Shield Coin
          </p>
        </div>
        {lastUpdated && (
          <div style={{
            color: '#6B6B6B', fontSize: 11,
          }}>
            Auto-refresh every 30s · Updated {lastUpdated.toLocaleTimeString()}
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24,
      }}>
        <StatCard
          label="TOTAL USERS"
          icon="👥"
          value={stats?.totalUsers.value ?? 0}
          delta={stats?.totalUsers.delta}
          accent="gold"
        />
        <StatCard
          label="ACTIVE MINERS"
          icon="⛏️"
          value={stats?.activeMiners.value ?? 0}
          accent="green"
        />
        <StatCard
          label="TOTAL MINED"
          icon="💎"
          value={(stats?.totalMined.value ?? 0).toFixed(2)}
          delta={stats?.totalMined.delta}
          accent="gold"
        />
        <StatCard
          label="TOTAL REFERRALS"
          icon="🔗"
          value={stats?.totalReferrals.value ?? 0}
          delta={stats?.totalReferrals.delta}
          accent="blue"
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <MiningChart data={sparkline} />
      </div>

      <ActiveMinersTable miners={miners} />
    </div>
  );
}
